import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useCare } from '../../contexts/CareContext'
import { PLANS } from '../../lib/plan'

const TIMES = [
  { value: 'morning', label: 'Morning 9–11am' },
  { value: 'afternoon', label: 'Afternoon 12–3pm' },
  { value: 'evening', label: 'Evening 4–6pm' },
]

// Built from PLANS so the prices stay defined in exactly one place.
const PLAN_CHOICES = [
  ...PLANS.map((p) => ({ value: p.key, label: `${p.name} ${p.price}${p.cadence}` })),
  { value: 'unsure', label: 'Not sure yet' },
]

/**
 * "Book a call" — every upgrade CTA in the app opens this.
 *
 * Writes a `consultations` row (status 'pending') and then notifies the team
 * by email. The row is the source of truth: if the email fails the request is
 * still recorded and the family still sees the success state.
 *
 * `plan` preselects the plan chip — pass a PLANS key ('connect' / 'plus') from
 * a priced CTA, or 'unsure' from a generic "book a call" one.
 */
export default function RequestCallModal({ onClose, plan }) {
  const { profile, user } = useAuth()
  const { recipient } = useCare()

  const [name, setName] = useState(profile?.full_name || '')
  const [email, setEmail] = useState(user?.email || profile?.email || '')
  const [phone, setPhone] = useState(profile?.phone || '')
  const [when, setWhen] = useState(null)
  const [planKey, setPlanKey] = useState(
    PLAN_CHOICES.some((p) => p.value === plan) ? plan : null
  )
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [sent, setSent] = useState(false)

  const recipientName =
    recipient?.preferred_name || recipient?.full_name || 'your parent'

  async function submit(e) {
    e.preventDefault()
    setErr('')

    const cleanName = name.trim()
    const cleanEmail = email.trim()
    const cleanPhone = phone.trim()

    if (!cleanName) {
      setErr('Please enter your name.')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setErr('Enter an email address we can reach you at.')
      return
    }
    // Deliberately loose: international formats vary and a wrong reject is
    // worse than a slightly messy number the team can still dial.
    if (cleanPhone.replace(/\D/g, '').length < 7) {
      setErr('Enter a phone number we can reach you on.')
      return
    }
    if (!when) {
      setErr('Pick a preferred call time.')
      return
    }
    if (!planKey) {
      setErr('Let us know which plan interests you.')
      return
    }

    setBusy(true)

    const timeLabel = TIMES.find((t) => t.value === when)?.label || when
    const planLabel = PLAN_CHOICES.find((p) => p.value === planKey)?.label || planKey

    const { error } = await supabase.from('consultations').insert({
      care_recipient_id: recipient.id,
      requested_by: user.id,
      status: 'pending',
      // `consultations` has no `notes` column — `consultation_notes` is the
      // only free-text field, so the callback details live here as JSON.
      // Preferred time and plan ride along too: this row is the source of
      // truth and the notification email is best-effort, so anything left out
      // here would be lost whenever Resend fails.
      consultation_notes: JSON.stringify({
        name: cleanName,
        phone: cleanPhone,
        preferred_time: timeLabel,
        plan_interest: planLabel,
      }),
      current_situation: `Requested a call back about daily check-ins for ${recipientName}.`,
    })

    if (error) {
      setBusy(false)
      setErr(
        error.message?.includes('row-level security')
          ? "You don't have permission to request a call for this profile."
          : 'Could not send that request. Please try again.'
      )
      return
    }

    // Best effort — never block the confirmation on the notification.
    try {
      await supabase.functions.invoke('send-consult-request', {
        body: {
          family_name: cleanName,
          family_email: cleanEmail,
          recipient_name: recipientName,
          phone: cleanPhone,
          preferred_time: timeLabel,
          plan: planLabel,
        },
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Consult notification not sent:', e)
    }

    setBusy(false)
    setSent(true)
  }

  return (
    <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-panel">
        {sent ? (
          <div className="stack center">
            <div
              aria-hidden
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'var(--green-light)',
                color: 'var(--green)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 30,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              ✓
            </div>
            <h2 className="title" style={{ fontSize: 22 }}>Request received</h2>
            <p style={{ fontSize: 15, lineHeight: 1.6 }}>
              We'll call you within one business day.
            </p>
            <button className="btn btn-block btn-lg" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form className="stack" onSubmit={submit}>
            <div className="spread">
              <h2 className="title" style={{ fontSize: 22 }}>Book a call</h2>
              <button
                type="button" onClick={onClose} aria-label="Close"
                style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 24, lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <p className="muted" style={{ fontSize: 15, lineHeight: 1.6 }}>
              A quick 20-minute call is the best next step. We'll learn about {recipientName}'s
              situation and set everything up together.
            </p>

            <div className="field">
              <label className="label" htmlFor="call-name">Name</label>
              <input
                id="call-name" type="text" className="input"
                autoComplete="name" placeholder="Your full name" required
                value={name} onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="call-email">Email</label>
              <input
                id="call-email" type="email" className="input" inputMode="email"
                autoComplete="email" placeholder="you@example.com" required
                value={email} onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="call-phone">Phone number</label>
              <input
                id="call-phone" type="tel" className="input" inputMode="tel"
                autoComplete="tel" placeholder="+1 604 000 0000" required
                value={phone} onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className="field">
              <span className="label" id="call-time-label">Preferred call time</span>
              <div className="row wrap" style={{ gap: 8 }} role="group" aria-labelledby="call-time-label">
                {TIMES.map((t) => (
                  <button
                    key={t.value} type="button"
                    className={`chip${when === t.value ? ' chip-on' : ''}`}
                    aria-pressed={when === t.value}
                    onClick={() => setWhen(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <span className="label" id="call-plan-label">Which plan interests you?</span>
              <div className="row wrap" style={{ gap: 8 }} role="group" aria-labelledby="call-plan-label">
                {PLAN_CHOICES.map((p) => (
                  <button
                    key={p.value} type="button"
                    className={`chip${planKey === p.value ? ' chip-on' : ''}`}
                    aria-pressed={planKey === p.value}
                    onClick={() => setPlanKey(p.value)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {err && <div className="error-text">{err}</div>}

            <button type="submit" className="btn btn-block btn-lg" disabled={busy}>
              {busy ? <span className="spinner" /> : 'Request a call back'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
